import * as Sentry from '@sentry/react'
import {useMutation} from '@tanstack/react-query'
import {useConfirmOrderApiMethod} from 'api/useConfirmOrderApiMethod'
import {get} from 'lodash'
import {usePaymentPath, useSiteUrl} from 'providers/EnvironmentProvider'
import {Params} from 'types'
import {delay} from 'utils/delay'

const MAX_TOO_MANY_REQUESTS = 3
const MAX_PAYMENT_PENDING = 10
const DEFAULT_DELAY = 1

export const useConfirmOrderMutation = () => {
  const siteUrl = useSiteUrl()
  const paymentPath = usePaymentPath()
  const confirmOrder = useConfirmOrderApiMethod()

  return useMutation({
    mutationFn: async ({order, orderIntent}: Params.ConfirmOrder) => {
      let retriesTooManyRequests = 1
      let retriesPaymentPending = 1

      while (true) {
        try {
          return await confirmOrder({order, orderIntent})
        } catch (error: any) {
          if (get(error, 'response.data.errorCode') === 'PAYMENT_PENDING') {
            // If Ventrata has not yet received a webhook from the payment gateway:
            Sentry.captureMessage('Ventrata: PAYMENT_PENDING', {
              level: 'info',
              extra: {
                action: 'confirmOrder',
                order: order.id,
                retries: retriesPaymentPending,
              },
            })

            if (retriesPaymentPending >= MAX_PAYMENT_PENDING) {
              Sentry.captureException(error)
              throw error
            }

            retriesPaymentPending++
            retriesTooManyRequests = 1

            await delay((DEFAULT_DELAY + 0.5 * retriesPaymentPending) * 1000)
            continue
          } else if (get(error, 'response.data.error') === 'TOO_MANY_REQUESTS') {
            // If Ventrata is just failing to process the request:
            Sentry.captureMessage('Ventrata: TOO_MANY_REQUESTS', {
              level: 'error',
              extra: {
                action: 'confirmOrder',
                order: order.id,
                retries: retriesTooManyRequests,
                retryAfter: get(error, 'response.data.retryAfter'),
              },
            })

            if (retriesTooManyRequests >= MAX_TOO_MANY_REQUESTS) {
              Sentry.captureException(error)
              throw error
            }

            retriesTooManyRequests++

            let thisDelay = DEFAULT_DELAY + 0.5 * retriesTooManyRequests
            let retryAfter = get(error, 'response.data.retryAfter')

            if (retryAfter) {
              thisDelay = retryAfter
            }

            await delay(thisDelay * 1000)
            continue
          } else if (get(error, 'response.data.errorCode') === 'ALREADY_CONFIRMED') {
            // If Ventrata says the order has already been confirmed:
            Sentry.captureMessage('Ventrata: ALREADY_CONFIRMED', {
              level: 'info',
              extra: {
                action: 'confirmOrder',
                order: order.id,
                retries: retriesTooManyRequests,
              },
            })

            return order
          } else if (get(error, 'response.data.errorCode') === 'PAYMENT_DECLINED') {
            // If Ventrata says the payment was declined:

            Sentry.captureMessage('Ventrata: PAYMENT_DECLINED', {
              level: 'info',
              extra: {
                action: 'confirmOrder',
                order: order.id,
              },
            })

            window.location.href = `${siteUrl}${paymentPath}`
          } else {
            Sentry.captureException(error)
            throw error
          }
        }
      }
    },
  })
}
